'use client';
import { useCallback, useState } from 'react';

import {
  Discount as DiscountIcon,
  PosCard,
  TruckFast,
} from '@/components/icons';
import { useLocale, useTranslations } from 'next-intl';
import Pricing from './pricing';
import CtaButtons from './cta-buttons';
import PaymentWidgets from '@/components/installments-widgets';
// import StockInfo from './stock-info';
import useCart from '@/store/cart.store';
import { useRouter } from '@/i18n/navigation';
import Media from './media';
import { handleConfigurableProduct } from '@/helpers/product';
import Configurable from './configurable';
import SpecialQtyCart from './special-qty-cart';
import useCheckout from '@/store/checkout.store';
import { getCookies, setCookie } from 'cookies-next';
import { toast } from 'sonner';
import AddToCart from '@/components/ui/buttons/add-to-cart';
import { Button } from '@/components/ui/buttons';
import CurrencyIcon from '@/components/ui/icons/currency-icon';
import Image from '@/components/image';
import NextLink from '@/components/next-link';
import { addItemToCart } from '@/services/cart';

const ProductPage = ({ product, parentCategory }) => {
  const t = useTranslations('');
  const locale = useLocale();
  const [curProduct, setCurProduct] = useState({ ...product });

  const { push } = useRouter();
  const [loading, setLoading] = useState(false);
  const cart = useCart((store) => store);
  const clearCheckout = useCheckout((state) => state.clearCheckout);

  const [quantity, setQuantity] = useState(1);

  const handleAddToCartFast = async () => {
    if (!curProduct?.pg_stock?.is_in_stock || curProduct?.pg_stock?.qty <= 0) {
      return;
    }
    setLoading(true);

    const { quote, mage } = getCookies();
    const res = await addItemToCart({
      sku: product?.sku,
      qty: quantity,
      quote,
      mage,
    });

    let productFeesFromCart = [];

    if (res?.status === 200) {
      if (quote !== res?.data?.quoteId) {
        cart.resetCart();
        clearCheckout();
        setCookie('quote', res?.data?.quoteId);
      }

      cart?.addItem({
        ...product,
        ...res?.data?.data,
        qty: res?.data?.data?.qty,
        productFeesFromCart,
      });
      toast.success(
        t(
          `common.${res?.data?.message}` ||
            'common.Item Added To Cart succssfully'
        )
      );
      setLoading(false);
      push('/checkout');
    } else {
      toast.error(
        t(
          `common.${res?.error?.error?.message?.replaceAll('.', '')}` ||
            'common.ErrorAddToCart'
        )
      );
      setLoading(false);
    }
  };

  const onChangeConfigurableProduct = useCallback(
    (selectedConfigurable) => {
      const findSelectedConfigurable = product?._configurables?.find(
        (config) => {
          return Object.keys(selectedConfigurable).every((key) => {
            return (
              config?.options?.[key] ===
              selectedConfigurable?.[key]?.option_value
            );
          });
        }
      );

      setCurProduct(
        handleConfigurableProduct(findSelectedConfigurable, product)
      );
    },
    [product]
  );

  return (
    <>
      {/* Image Gallery Done */}
      <Media
        images={curProduct?._media_?.image || []}
        labels={product?.label}
        name={curProduct?.name?.[0]}
        locale={locale}
      />

      <div className='relative max-sm:pt-9'>
        {/* Product Details */}
        <div className='flex items-center gap-2'>
          {product?.brand_logo && (
            <Image
              src={product?.brand_logo}
              className='size-6 rounded'
              alt={product?.option_text_brands?.at(0)}
            />
          )}
          <NextLink
            href={
              `/${parentCategory?.[0]?.url}?brands=${product?.brands?.at(0)}` ||
              '#'
            }
            className={`block font-bold text-[#3e95ed] underline max-md:text-sm max-sm:text-xs lg:leading-[32px]`}
          >
            {product?.option_text_brands?.at(0) || ''}
          </NextLink>
        </div>
        <h1 className='text-2xl leading-8 font-semibold max-lg:text-xl max-md:text-lg max-sm:text-base'>
          {curProduct?.name}
        </h1>
        <div className='my-2.5 flex items-center gap-2 max-lg:hidden max-sm:text-sm'>
          <span className='text-zinc-400'>{t('common.sku_key')} :</span>
          <span>{curProduct?.sku}</span>
        </div>

        {/* Pricing Section */}
        <Pricing price={curProduct?.prices_with_tax} />

        {/* Configurable Section */}
        {curProduct?.configurable_options?.length > 0 && (
          <Configurable
            current_option={curProduct?.current_option}
            configurable_options={curProduct?.configurable_options}
            onChangeConfigurableProduct={onChangeConfigurableProduct}
          />
        )}

        {/* Quantity Selector */}
        <SpecialQtyCart
          quantity={quantity}
          setQuantity={setQuantity}
          availableQty={curProduct?.pg_stock?.qty}
        />

        {/* Payment Options */}
        <div className='border-border rounded-lg border px-4.5 py-[15px] max-lg:mt-5 max-lg:py-3 max-sm:px-4 max-sm:py-[11px]'>
          <p className='mb-[18px] flex items-center gap-4 font-semibold'>
            <PosCard className='max-md:size-[25px] max-sm:size-[22px]' />
            <span className='font-semibold max-md:text-sm'>
              {t('product.buy_now_pay_later')}:
            </span>
          </p>
          <PaymentWidgets product={curProduct} fromPDP={true} />
        </div>

        {/* Delivery Info */}
        <div className='border-border flex items-center gap-2 rounded-lg border px-5 py-4.5 max-md:py-3 max-md:text-sm max-sm:my-3.5 max-sm:px-5 max-sm:py-[11px] max-sm:text-xs lg:mt-[15px] lg:mb-[21px]'>
          <p className='flex items-center gap-2'>
            <TruckFast className='max-sm:size-5' /> {t('common.delivery_key')}:
          </p>
          <span>
            {t('common.delivery_time', {
              minDays: 1,
              maxDays: 4,
              city: locale == 'en' ? 'Riyadh' : 'الرياض',
            })}
          </span>
        </div>
        {/* Stock Info */}
        {/* <StockInfo entity_id={curProduct?.entity_id} /> */}
        {/* Offers Section */}
        {curProduct?.cart_rule?.length ? (
          <div className='border-border !my-5 rounded-lg border p-5 pt-3 max-sm:mt-[15px] max-sm:mb-3 max-sm:px-4 max-sm:py-3'>
            {/* Offers Header */}
            <div className='flex items-center gap-4.5 font-semibold max-sm:gap-2.5'>
              <DiscountIcon className='max-md:size-6 max-sm:size-5' />
              <span className='max-lg:text-sm max-sm:text-[13px]'>
                {t('common.offers_key')}
              </span>
            </div>

            {/* Offer Details */}
            <div className='mt-2 space-y-2 max-sm:text-[10px] sm:mt-3 sm:space-y-3 md:text-sm lg:text-base'>
              {curProduct?.cart_rule
                .filter(({ from_date, to_date, is_active }) => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);

                  const from = new Date(from_date);
                  const to = new Date(to_date);
                  from.setHours(0, 0, 0, 0);
                  to.setHours(0, 0, 0, 0);

                  return is_active === '1' && today >= from && today <= to;
                })
                .map(
                  ({ discount_amount, name, description, to_date }, index) => (
                    <ul
                      key={index}
                      className='border-border flex flex-wrap items-center gap-x-2 border-b pb-2 last:border-b-0 last:pb-0'
                    >
                      <li className='text-primary'>
                        <span className='font-semibold'>
                          {locale == 'en' ? 'Discount' : 'حصم'}
                        </span>
                        <span className='font-semibold'>
                          {discount_amount / 100}%
                        </span>
                      </li>
                      <li className='font-semibold'>{name}</li>
                      <li className='font-semibold'>{description}</li>
                      <li className='flex flex-wrap items-center'>
                        <CurrencyIcon className='size-3 max-sm:size-2' />
                        <span className='font-semibold'>{discount_amount}</span>
                      </li>
                      <li className=''>
                        <span className='font-smeibold'>
                          {locale === 'en' ? 'Until' : 'حتى'} :
                        </span>
                        <span className='ms-[8px] animate-pulse font-semibold'>
                          {to_date}
                        </span>
                      </li>
                    </ul>
                  )
                )}
            </div>
          </div>
        ) : null}
        {/* Buttons Section */}
        <div className='my-5 flex gap-4 max-sm:my-3'>
          <Button
            disabled={
              loading ||
              !(product?.pg_stock?.is_in_stock && product?.pg_stock?.qty >= 1)
            }
            onClick={() => handleAddToCartFast()}
            className='border-border w-full border bg-white font-bold !text-black hover:bg-gray-100 hover:!text-white disabled:!text-white max-md:text-sm max-sm:border-black max-sm:font-semibold'
          >
            {!(product?.pg_stock?.is_in_stock && product?.pg_stock?.qty >= 1)
              ? t('product.Out_of_stock')
              : t('product.buy_now_key')}
          </Button>

          <AddToCart
            {...{
              isFromPDP: true,
              product: curProduct,
              qty: quantity,
              fromCartPopUp: false,
            }}
          />
        </div>
        {/* Favorite & Share */}
        <CtaButtons
          title={curProduct?.name}
          sku={curProduct?.sku}
          product={product}
          description={curProduct?.data?.[0]?.meta_description}
        />
      </div>
    </>
  );
};

// Default Props
ProductPage.defaultProps = {
  sku: '',
  name: '',
  price: {},
  description: '',
  colors: [],
};

export default ProductPage;
